/**
 * The rules behind the two reset screens. No React — the convention's own test for
 * whether logic belongs in a model.
 *
 * ## WHAT IS NOT IN THIS FILE, and the absence is the feature
 *
 * No token parsing. The rejected implementation needed a `tokenFromFragment` here,
 * tolerant of three fragment shapes, because the token was ONDO's own and an endpoint
 * of ours judged it. Supabase's recovery link puts its tokens in the fragment and the
 * browser client is built with `detectSessionInUrl: true`, so **the client consumes
 * them itself and the result is an ordinary session.** There is nothing for a model to
 * read and nothing for a screen to interpret, which is the whole of what using the
 * identity provider bought: the hardest, most security-sensitive function in the old
 * design does not exist.
 *
 * What is left is the password rule, which lives in `@ondo/shared` because the phone
 * and the API need the same one, and the two view unions below.
 */

export { refusePassword, MIN_PASSWORD_LENGTH } from "@ondo/shared";
export type { PasswordRefusal } from "@ondo/shared";

/** What the request-a-link screen is doing. */
export type ForgotView =
  | { kind: "asking"; email: string; canSubmit: boolean; error: string | null }
  | { kind: "sending"; email: string }
  /**
   * Asked. Carries NOTHING about the address, and that is the type doing the work.
   *
   * There is no field here for a verdict, so a screen cannot render one even by
   * mistake — the sentence is a constant and the union has nowhere to put an
   * alternative. The rejected design carried the server's `message` on this member for
   * the same purpose; with no server to ask, making the state empty is the stronger
   * version of the same idea.
   */
  | { kind: "sent" };

/**
 * What the set-a-new-password screen is doing.
 *
 * `opening` exists because establishing the session from the link's fragment is
 * asynchronous: for a moment the page genuinely does not know whether it has one. A
 * state rather than a silent gap, so the screen can say what it is waiting for instead
 * of flashing an empty form.
 *
 * `unusable` is "you did not arrive here from the email" — a direct visit, or a link
 * whose fragment was already spent, which is OND-118's "tapped twice" case. It is a
 * different thing from `error` on the form, which is the server refusing the change
 * itself; one means "open the link again", the other "ask for a new one". Collapsing
 * them sends somebody to the wrong remedy.
 */
export type ResetView =
  | { kind: "opening" }
  | { kind: "unusable" }
  | {
      kind: "setting";
      password: string;
      confirmation: string;
      canSubmit: boolean;
      /** Set once the user has tried, so an untouched form is not scolded. */
      error: string | null;
      /** Which rule is unmet, for a hint under the fields. Null once they are met. */
      refusal: "too-short" | "mismatch" | null;
    }
  | { kind: "saving" }
  | { kind: "done" };

/**
 * Whether a failure was "wait" rather than "no".
 *
 * `AuthError` carries a `status` and 429 is the one that matters: a throttle is not a
 * verdict about the account, and showing it as a failure tells the user the opposite of
 * the truth — the rule `RoleSignIn` already keeps about its own 429.
 *
 * Read structurally rather than with an `instanceof`, because `@ondo/supabase/browser`
 * re-exports the auth library's TYPES and not its constructors — deliberately, so apps
 * do not reach past the package — leaving nothing to compare against.
 */
export function isThrottled(cause: unknown): boolean {
  return (
    typeof cause === "object" &&
    cause !== null &&
    (cause as { status?: unknown }).status === 429
  );
}
